import { useEffect, useState } from "react";
import { useGlobalViewMode } from "../components/ViewModeToggle";

import { PageHeader } from "../components/GradientCard";import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { moduleCanDo } from "../lib/modules";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Eye, Lock, ArrowRight, CheckCircle2, XCircle, Copy, Mail, Send, TrendingUp } from "lucide-react";

const STAGE_LABELS = {
  employee_submitted: ["bg-violet-100 text-violet-800", "Employee Submitted"],
  manager_assigned: ["bg-amber-100 text-amber-800", "Awaiting Manager"],
  manager_reviewed: ["bg-sky-100 text-sky-800", "Manager Reviewed"],
  hr_assigned: ["bg-amber-100 text-amber-800", "Awaiting HR"],
  hr_reviewed: ["bg-sky-100 text-sky-800", "HR Reviewed"],
  approved: ["bg-emerald-100 text-emerald-800", "Approved"],
  rejected: ["bg-rose-100 text-rose-800", "Rejected"],
};

const fmtINR = (n) => "₹ " + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

function StageBadge({ stage }) {
  const [cls, lbl] = STAGE_LABELS[stage] || ["bg-slate-100", stage];
  return <Badge className={cls}>{lbl}</Badge>;
}

export default function SalaryHike({ embedded = false } = {}) {
  const { user } = useAuth();
  const [viewMode] = useGlobalViewMode();
  const isAdmin = user?.role === "master_admin" || moduleCanDo(user, "hr", "edit");
  const [rows, setRows] = useState([]);
  const [viewing, setViewing] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/salary-hike/requests"); setRows(data || []); }
    catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="salary-hike-page">
      {!embedded && (
        <PageHeader
          moduleKey="hr"
          tone="violet"
          icon={TrendingUp}
          eyebrow="HR Module"
          title="Salary Hike"
          subtitle="Review and route salary increment requests through Manager → HR → Master Admin approval."
        />
      )}

      <div className="pk-card overflow-hidden">
        {viewMode === "card" ? (
          rows.length === 0 ? (
            <div className="p-12 text-center text-slate-400 text-sm">No salary hike requests yet.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 p-3" data-testid="sh-cards">
              {rows.map((r) => (
                <div key={r.id} className="pk-card p-4 hover:shadow-md transition-shadow" data-testid={`sh-card-${r.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-slate-800 truncate">{r.employee_name}</div>
                      <div className="text-[10.5px] text-slate-500 mono truncate">{r.employee_email}</div>
                    </div>
                    <StageBadge stage={r.stage} />
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-[11.5px] mb-2">
                    <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Current</div><div className="mono text-slate-800">{fmtINR(r.current_salary)}</div></div>
                    <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Requested</div><div className="text-slate-800 truncate">{r.employee_form?.requested_percentage_or_amount || "—"}</div></div>
                    <div className="col-span-2"><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Submitted</div><div className="text-[11px] text-slate-600 mono">{(r.created_at || "").slice(0, 10)}</div></div>
                  </div>
                  <div className="pt-2 border-t border-slate-100">
                    <Button size="sm" variant="outline" onClick={() => setViewing(r)} className="h-7 text-[10.5px] w-full" data-testid={`sh-open-${r.id}`}>
                      <Eye size={11} className="mr-1" /> Open
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-600">
                <th className="px-4 py-3 font-semibold">Employee</th>
                <th className="px-4 py-3 font-semibold">Current Salary</th>
                <th className="px-4 py-3 font-semibold">Requested</th>
                <th className="px-4 py-3 font-semibold">Stage</th>
                <th className="px-4 py-3 font-semibold">Submitted</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={6} className="text-center py-10 text-slate-400 text-sm">No salary hike requests yet.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-100" data-testid={`sh-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold">{r.employee_name}</div>
                    <div className="text-[10.5px] text-slate-500 mono">{r.employee_email}</div>
                  </td>
                  <td className="px-4 py-3 mono text-[12.5px]">{fmtINR(r.current_salary)}</td>
                  <td className="px-4 py-3 text-[12.5px]">{r.employee_form?.requested_percentage_or_amount || "—"}</td>
                  <td className="px-4 py-3"><StageBadge stage={r.stage} /></td>
                  <td className="px-4 py-3 text-[11.5px] text-slate-600">{(r.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="outline" onClick={() => setViewing(r)} className="h-8 text-[11px]" data-testid={`sh-open-${r.id}`}>
                      <Eye size={12} className="mr-1" /> Open
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>

      {viewing && (
        <SalaryHikeDetail
          rid={viewing.id}
          onClose={() => setViewing(null)}
          onReload={load}
          isAdmin={isAdmin}
          isMaster={user?.role === "master_admin"}
          currentUserId={user?.id}
        />
      )}
    </div>
  );
}

function SalaryHikeDetail({ rid, onClose, onReload, isAdmin, isMaster, currentUserId }) {
  const [d, setD] = useState(null);
  const [employees, setEmployees] = useState([]);
  // Admin "Edit" flags — when true, render the form even if the stage is past it.
  const [editingManager, setEditingManager] = useState(false);
  const [editingHR, setEditingHR] = useState(false);
  const [editingFinal, setEditingFinal] = useState(false);
  const refresh = async () => {
    try {
      const { data } = await api.get(`/salary-hike/requests/${rid}`);
      setD(data);
    } catch (e) { toast.error(formatError(e)); onClose(); }
  };
  useEffect(() => {
    refresh();
    if (isAdmin) api.get("/employees").then(({ data }) => setEmployees((data || []).filter((e) => e.is_active))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rid]);
  if (!d) return null;
  const reload = async () => {
    setEditingManager(false); setEditingHR(false); setEditingFinal(false);
    await refresh(); onReload && onReload();
  };

  const stage = d.stage;
  const isManagerForThis = d.manager_reviewer_id === currentUserId;
  const isHRForThis = d.hr_reviewer_id === currentUserId;
  const hasManagerForm = d.manager_form && Object.keys(d.manager_form || {}).length > 0;
  const hasHRForm = d.hr_form && Object.keys(d.hr_form || {}).length > 0;

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="sh-detail-dialog">
        <DialogHeader>
          <DialogTitle className="heading flex items-center gap-2 flex-wrap">
            {d.employee_name} <StageBadge stage={d.stage} />
          </DialogTitle>
          <div className="text-[12px] text-slate-600">
            {d.employee_email} · Current Salary <b className="mono">{fmtINR(d.current_salary)}</b>
          </div>
        </DialogHeader>

        <EmployeeFormView d={d} />

        {/* Stage 2 — Manager: assign (admin) + form (assigned manager OR any admin from
            any non-terminal pre-stage). */}
        {(stage === "employee_submitted" || (isAdmin && stage === "manager_assigned" && !d.manager_reviewer_name)) && isAdmin && (
          <AssignReviewerCard label="Assign Manager Reviewer" rid={rid} kind="manager" employees={employees} onSaved={reload} />
        )}
        {(((stage === "manager_assigned") || (isAdmin && stage === "employee_submitted")) && (isManagerForThis || isAdmin) && !hasManagerForm) || editingManager ? (
          <ManagerReviewForm rid={rid} d={d} onSaved={reload} disabled={!isManagerForThis && !isAdmin} />
        ) : null}
        {hasManagerForm && !editingManager && (
          <ManagerReviewView d={d} isAdmin={isAdmin} onEdit={() => setEditingManager(true)} />
        )}

        {/* Stage 3 — HR */}
        {(stage === "manager_reviewed" || (isAdmin && stage === "hr_assigned" && !d.hr_reviewer_name)) && isAdmin && (
          <AssignReviewerCard label="Assign HR Reviewer" rid={rid} kind="hr" employees={employees} onSaved={reload} />
        )}
        {(((stage === "hr_assigned") || (isAdmin && stage === "manager_reviewed")) && (isHRForThis || isAdmin) && !hasHRForm) || editingHR ? (
          <HRReviewForm rid={rid} d={d} onSaved={reload} disabled={!isHRForThis && !isAdmin} initial={editingHR ? d.hr_form : undefined} />
        ) : null}
        {hasHRForm && !editingHR && (
          <HRReviewView d={d} isAdmin={isAdmin} onEdit={() => setEditingHR(true)} />
        )}

        {/* Stage 4 — Final approval. */}
        {((stage === "hr_reviewed" || (isAdmin && stage !== "approved" && stage !== "rejected")) && isAdmin) || editingFinal ? (
          <FinalApprovalForm rid={rid} d={d} onSaved={reload} />
        ) : null}
        {stage === "approved" && !editingFinal && (
          <>
            <ApprovedCard d={d} />
            {isAdmin && (
              <div className="text-right">
                <Button size="sm" variant="outline" onClick={() => setEditingFinal(true)} className="h-7 text-[10.5px]" data-testid="sh-edit-final">
                  Edit Final Approval
                </Button>
              </div>
            )}
          </>
        )}
        {stage === "rejected" && (
          <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-[12.5px] text-rose-800">
            <b>Rejected by {d.rejected_by}</b> · {(d.rejected_at || "").slice(0, 16).replace("T", " ")}
            {d.rejected_reason && <div className="mt-1 italic">&ldquo;{d.rejected_reason}&rdquo;</div>}
          </div>
        )}

        {/* Admin actions */}
        {isAdmin && (
          <AdminControls rid={rid} d={d} onSaved={reload} />
        )}

        <details className="border-t border-slate-100 pt-2">
          <summary className="text-[11px] text-slate-500 cursor-pointer">Activity Log ({(d.log || []).length})</summary>
          <ul className="text-[11.5px] mt-2 space-y-1">
            {(d.log || []).slice().reverse().map((l, i) => (
              <li key={i} className="border-l-2 border-slate-200 pl-2"><b>{l.by}</b> <span className="text-slate-500">— {l.at.slice(0, 16).replace("T", " ")}</span><br />{l.event}</li>
            ))}
          </ul>
        </details>

        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FormViewRow({ label, value }) {
  return (
    <div className="grid grid-cols-[200px_1fr] gap-2 text-[12.5px] py-1 border-b border-slate-100">
      <div className="text-slate-500 uppercase text-[10.5px] tracking-wider font-semibold">{label}</div>
      <div className="text-slate-800 whitespace-pre-wrap">{value || <span className="text-slate-400 italic">&mdash;</span>}</div>
    </div>
  );
}

function EmployeeFormView({ d }) {
  const f = d.employee_form || {};
  return (
    <div className="border border-slate-200 rounded-md p-4 bg-violet-50/30" data-testid="sh-emp-form-view">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-violet-700 mb-2">Employee Submission</div>
      <FormViewRow label="Reason" value={f.reason} />
      <FormViewRow label="Achievements" value={f.achievements} />
      <FormViewRow label="Additional Responsibilities" value={f.additional_responsibilities} />
      <FormViewRow label="Measurable Contributions" value={Array.isArray(f.measurable_contributions) ? f.measurable_contributions.join(", ") : f.measurable_contributions} />
      <FormViewRow label="Certifications / Training" value={f.certifications} />
      <FormViewRow label="Challenges Overcome" value={f.challenges_overcome} />
      <FormViewRow label="Next 6-12 Month Goals" value={f.next_goals} />
      <FormViewRow label="Requested %/Amount" value={f.requested_percentage_or_amount} />
      <FormViewRow label="Justification" value={f.justification} />
    </div>
  );
}

function AssignReviewerCard({ label, rid, kind, employees, onSaved }) {
  const [pick, setPick] = useState("");
  const submit = async () => {
    if (!pick) return toast.error("Pick a reviewer");
    try {
      const path = kind === "manager" ? "assign-manager" : "assign-hr";
      await api.post(`/salary-hike/requests/${rid}/${path}`, { reviewer_id: pick });
      toast.success("Reviewer assigned");
      onSaved();
    } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="border border-amber-300 bg-amber-50 rounded-md p-3 flex items-center gap-2 flex-wrap" data-testid={`sh-assign-${kind}`}>
      <div className="text-[12.5px] font-semibold text-amber-900">{label}:</div>
      <Select value={pick} onValueChange={setPick}>
        <SelectTrigger className="w-[260px] h-9" data-testid={`sh-${kind}-select`}><SelectValue placeholder="Pick employee…" /></SelectTrigger>
        <SelectContent className="max-h-[260px]">{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} ({e.role})</SelectItem>)}</SelectContent>
      </Select>
      <Button size="sm" onClick={submit} className="h-9 bg-violet-600 hover:bg-violet-700" data-testid={`sh-${kind}-assign-btn`}>
        <ArrowRight size={12} className="mr-1" /> Move to Next Stage
      </Button>
    </div>
  );
}

function ManagerReviewForm({ rid, d, onSaved, disabled }) {
  const currentSalary = Number(d.current_salary || 0);
  const [f, setF] = useState({
    recommendation: "", rating: "", achievements: "", additional_responsibilities: "",
    recommended_percentage: "", recommended_salary: "", remarks: "",
  });
  const set = (k, v) => {
    const next = { ...f, [k]: v };
    // Auto-calculate Revised Salary when % is typed. HR can still manually
    // override the value afterwards (a manual change to `recommended_salary`
    // simply replaces the calculated number).
    if (k === "recommended_percentage" && currentSalary > 0) {
      const pct = Number(v);
      if (Number.isFinite(pct) && pct >= 0) {
        next.recommended_salary = String(Math.round(currentSalary * (1 + pct / 100)));
      }
    }
    setF(next);
  };
  const submit = async () => {
    try {
      await api.post(`/salary-hike/requests/${rid}/submit-manager-review`, { form: f });
      toast.success("Manager review submitted");
      onSaved();
    } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="border border-violet-300 bg-violet-50/30 rounded-md p-4 space-y-2.5" data-testid="sh-manager-form">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-violet-700">
        Reporting Manager Review &mdash; {d.manager_reviewer_name}
      </div>
      <div className="text-[11px] text-slate-600">
        Current Salary on record: <b className="mono">&#8377; {currentSalary.toLocaleString("en-IN")}</b>
      </div>
      <FldSelect label="Does the manager recommend the salary increment?" value={f.recommendation} onChange={(v) => set("recommendation", v)} opts={["Yes", "No", "Conditional"]} tid="mgr-rec" />
      <FldSelect label="Employee Performance Rating" value={f.rating} onChange={(v) => set("rating", v)} opts={["Outstanding", "Exceeds Expectations", "Meets Expectations", "Needs Improvement"]} tid="mgr-rating" />
      <FldText label="Key achievements observed by the manager" value={f.achievements} onChange={(v) => set("achievements", v)} tid="mgr-ach" />
      <FldText label="Additional responsibilities handled by the employee" value={f.additional_responsibilities} onChange={(v) => set("additional_responsibilities", v)} tid="mgr-resp" />
      <FldInput label="Recommended Increment Percentage (%)" type="number" value={f.recommended_percentage} onChange={(v) => set("recommended_percentage", v)} tid="mgr-pct" />
      <FldInput
        label={`Recommended Revised Salary (\u20B9) ${f.recommended_percentage ? "\u2014 auto-calculated, override allowed" : ""}`}
        type="number"
        value={f.recommended_salary}
        onChange={(v) => set("recommended_salary", v)}
        tid="mgr-sal"
      />
      <FldText label="Manager Remarks" value={f.remarks} onChange={(v) => set("remarks", v)} tid="mgr-rem" />
      <div className="flex justify-end">
        <Button onClick={submit} disabled={disabled} className="bg-violet-600 hover:bg-violet-700" data-testid="sh-mgr-submit">Submit Review</Button>
      </div>
    </div>
  );
}

function ManagerReviewView({ d, onEdit, isAdmin }) {
  const f = d.manager_form || {};
  return (
    <div className="border border-sky-200 bg-sky-50/30 rounded-md p-4" data-testid="sh-manager-view">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-sky-700">
          Manager Review &middot; {d.manager_reviewer_name} &middot; {(d.manager_submitted_at || "").slice(0, 10)}
        </div>
        {isAdmin && onEdit && (
          <Button size="sm" variant="outline" onClick={onEdit} className="h-7 text-[10.5px]" data-testid="sh-edit-manager">
            Edit
          </Button>
        )}
      </div>
      <div className="text-[12.5px] space-y-1">
        <div><b>Recommendation:</b> {f.recommendation} &middot; <b>Rating:</b> {f.rating}</div>
        <div><b>Achievements:</b> {f.achievements}</div>
        <div><b>Additional Responsibilities:</b> {f.additional_responsibilities}</div>
        <div><b>Recommended:</b> {f.recommended_percentage}% &rarr; &#8377;{f.recommended_salary}</div>
        {f.remarks && <div className="italic text-slate-600">&ldquo;{f.remarks}&rdquo;</div>}
      </div>
    </div>
  );
}

function HRReviewForm({ rid, d, onSaved, disabled, initial }) {
  const currentSalary = Number(d?.current_salary || 0);
  const [f, setF] = useState(initial || {
    benchmark: "", budget_availability: "", recommended_percentage: "",
    recommended_salary: "", effective_date: "", remarks: "",
  });
  const set = (k, v) => {
    const next = { ...f, [k]: v };
    if (k === "recommended_percentage" && currentSalary > 0) {
      const pct = Number(v);
      if (Number.isFinite(pct) && pct >= 0) {
        next.recommended_salary = String(Math.round(currentSalary * (1 + pct / 100)));
      }
    }
    setF(next);
  };
  const submit = async () => {
    try {
      await api.post(`/salary-hike/requests/${rid}/submit-hr-review`, { form: f });
      toast.success("HR review submitted");
      onSaved();
    } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="border border-violet-300 bg-violet-50/30 rounded-md p-4 space-y-2.5" data-testid="sh-hr-form">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-violet-700">HR Review</div>
      <div className="text-[11px] text-slate-600">
        Current Salary on record: <b className="mono">&#8377; {currentSalary.toLocaleString("en-IN")}</b>
      </div>
      <FldInput label="Current Salary Benchmark for Similar Role (\u20B9)" type="number" value={f.benchmark} onChange={(v) => set("benchmark", v)} tid="hr-bench" />
      <FldSelect label="Budget Availability" value={f.budget_availability} onChange={(v) => set("budget_availability", v)} opts={["Available", "Tight", "Not Available"]} tid="hr-budget" />
      <FldInput label="Recommended Increment (%)" type="number" value={f.recommended_percentage} onChange={(v) => set("recommended_percentage", v)} tid="hr-pct" />
      <FldInput
        label={`Recommended Revised Salary (\u20B9) ${f.recommended_percentage ? "\u2014 auto-calculated, override allowed" : ""}`}
        type="number"
        value={f.recommended_salary}
        onChange={(v) => set("recommended_salary", v)}
        tid="hr-rec-sal"
      />
      <FldInput label="Effective Date of Increment" type="date" value={f.effective_date} onChange={(v) => set("effective_date", v)} tid="hr-date" />
      <FldText label="HR Remarks" value={f.remarks} onChange={(v) => set("remarks", v)} tid="hr-rem" />
      <div className="flex justify-end">
        <Button onClick={submit} disabled={disabled} className="bg-violet-600 hover:bg-violet-700" data-testid="sh-hr-submit">Submit Review</Button>
      </div>
    </div>
  );
}

function HRReviewView({ d, onEdit, isAdmin }) {
  const f = d.hr_form || {};
  return (
    <div className="border border-sky-200 bg-sky-50/30 rounded-md p-4" data-testid="sh-hr-view">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-sky-700">
          HR Review &middot; {d.hr_reviewer_name} &middot; {(d.hr_submitted_at || "").slice(0, 10)}
        </div>
        {isAdmin && onEdit && (
          <Button size="sm" variant="outline" onClick={onEdit} className="h-7 text-[10.5px]" data-testid="sh-edit-hr">
            Edit
          </Button>
        )}
      </div>
      <div className="text-[12.5px] space-y-1">
        <div><b>Benchmark:</b> &#8377;{f.benchmark}</div>
        <div><b>Budget:</b> {f.budget_availability} &middot; <b>Recommended:</b> {f.recommended_percentage}% &rarr; &#8377;{f.recommended_salary}</div>
        <div><b>Effective from:</b> {f.effective_date}</div>
        {f.remarks && <div className="italic text-slate-600">&ldquo;{f.remarks}&rdquo;</div>}
      </div>
    </div>
  );
}

function FinalApprovalForm({ rid, d, onSaved }) {
  const currentSalary = Number(d.current_salary || 0);
  const [f, setF] = useState({
    approved_percentage: d.final_form?.approved_percentage || d.hr_form?.recommended_percentage || "",
    approved_salary: d.final_form?.approved_salary || "",
    effective_date: d.final_form?.effective_date || d.hr_form?.effective_date || "",
    final_comments: d.final_form?.final_comments || "",
  });
  const set = (k, v) => {
    const next = { ...f, [k]: v };
    if (k === "approved_percentage" && currentSalary > 0) {
      const pct = Number(v);
      if (Number.isFinite(pct) && pct >= 0) {
        next.approved_salary = String(Math.round(currentSalary * (1 + pct / 100)));
      }
    }
    setF(next);
  };
  // Pre-populate the auto-calculated salary on first mount when % is already set.
  useEffect(() => {
    if (f.approved_percentage && !f.approved_salary && currentSalary > 0) {
      const pct = Number(f.approved_percentage);
      if (Number.isFinite(pct)) {
        setF((prev) => ({ ...prev, approved_salary: String(Math.round(currentSalary * (1 + pct / 100))) }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const submit = async () => {
    try {
      await api.post(`/salary-hike/requests/${rid}/final-approve`, { form: f });
      toast.success(d.stage === "approved" ? "Final approval updated" : "Final approval recorded");
      onSaved();
    } catch (e) { toast.error(formatError(e)); }
  };
  const isEdit = d.stage === "approved";
  return (
    <div className="border-2 border-emerald-300 bg-emerald-50/40 rounded-md p-4 space-y-2.5" data-testid="sh-final-form">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-emerald-700">
        Final Approval (Master / HR Admin){isEdit ? " \u2014 EDIT MODE" : ""}
      </div>
      <div className="text-[11px] text-slate-600">
        Current Salary on record: <b className="mono">&#8377; {currentSalary.toLocaleString("en-IN")}</b>
      </div>
      <FldInput label="Approved Increment (%)" type="number" value={f.approved_percentage} onChange={(v) => set("approved_percentage", v)} tid="fin-pct" />
      <FldInput
        label={`Approved Revised Salary (\u20B9) ${f.approved_percentage ? "\u2014 auto-calculated, override allowed" : ""}`}
        type="number"
        value={f.approved_salary}
        onChange={(v) => set("approved_salary", v)}
        tid="fin-sal"
      />
      <FldInput label="Effective Date" type="date" value={f.effective_date} onChange={(v) => set("effective_date", v)} tid="fin-date" />
      <FldText label="Final Comments" value={f.final_comments} onChange={(v) => set("final_comments", v)} tid="fin-cmt" />
      <div className="flex justify-end">
        <Button onClick={submit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="sh-final-submit">
          <CheckCircle2 size={14} className="mr-1.5" /> {isEdit ? "Update Approval" : "Approve & Update Salary"}
        </Button>
      </div>
    </div>
  );
}

function ApprovedCard({ d }) {
  const f = d.final_form || {};
  const subject = `Salary Increment Letter — ${d.employee_name}`;
  const body = `Dear ${d.employee_name},

We are pleased to inform you that following a comprehensive review of your performance, your salary has been revised effective ${f.effective_date}.

Previous Salary: ₹${Number(d.current_salary || 0).toLocaleString("en-IN")}
Approved Increment: ${f.approved_percentage}%
Revised Monthly Salary: ₹${Number(f.approved_salary || 0).toLocaleString("en-IN")}

${f.final_comments ? `Comments from Management:\n${f.final_comments}\n\n` : ""}We appreciate your continued contributions and look forward to your continued success with the team.

Warm Regards,
HR Department — Panchakosh Ventures`;
  return (
    <div className="border-2 border-emerald-300 bg-emerald-50/30 rounded-md p-4" data-testid="sh-approved-card">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-emerald-700 mb-2">
        ✓ Approved by {d.final_approved_by} · {(d.final_approved_at || "").slice(0, 10)}
      </div>
      <div className="text-[13px] mb-3">
        <b>{f.approved_percentage}%</b> increment → New salary: <b className="mono">{fmtINR(f.approved_salary)}</b>
        <span className="text-slate-500"> · effective {f.effective_date}</span>
      </div>
      <div className="border-t border-slate-200 pt-3">
        <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-600 mb-2">Increment Letter — ready to email</div>
        <div className="text-[12px] mb-1.5"><b>To:</b> <span className="mono">{d.employee_email}</span></div>
        <div className="text-[12px] mb-1.5"><b>Subject:</b> {subject}</div>
        <pre className="text-[11.5px] bg-white border border-slate-200 rounded p-3 whitespace-pre-wrap font-sans max-h-[260px] overflow-y-auto" data-testid="sh-letter-body">{body}</pre>
        <div className="flex flex-wrap gap-1.5 mt-2">
          <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(d.employee_email).then(() => toast.success("Email copied"))} className="h-7 text-[10.5px]" data-testid="sh-copy-email"><Copy size={11} className="mr-1" /> Copy Email</Button>
          <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(subject).then(() => toast.success("Subject copied"))} className="h-7 text-[10.5px]" data-testid="sh-copy-subject"><Copy size={11} className="mr-1" /> Copy Subject</Button>
          <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(body).then(() => toast.success("Body copied"))} className="h-7 text-[10.5px]" data-testid="sh-copy-body"><Copy size={11} className="mr-1" /> Copy Body</Button>
          <Button size="sm" onClick={() => { window.location.href = `mailto:${d.employee_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`; }} className="h-7 text-[10.5px] bg-violet-600 hover:bg-violet-700" data-testid="sh-send-email"><Send size={11} className="mr-1" /> Send Email</Button>
        </div>
      </div>
    </div>
  );
}

function AdminControls({ rid, d, onSaved }) {
  const [until, setUntil] = useState("");
  const [reason, setReason] = useState("");
  const lock = async () => {
    try { await api.post(`/salary-hike/requests/${rid}/lock-employee`, { locked_until: until }); toast.success(until ? "Lock set" : "Lock cleared"); onSaved(); }
    catch (e) { toast.error(formatError(e)); }
  };
  const reject = async () => {
    if (!window.confirm("Reject this request?")) return;
    try { await api.post(`/salary-hike/requests/${rid}/reject`, { reason }); toast.success("Rejected"); onSaved(); }
    catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="border-t border-slate-100 pt-3 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="sh-admin-controls">
      <div className="border border-slate-200 rounded-md p-3">
        <Label className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-600 flex items-center gap-1"><Lock size={11} /> Lock employee until</Label>
        <div className="flex gap-1.5 mt-1.5">
          <Input type="date" value={until} onChange={(e) => setUntil(e.target.value)} className="h-8 text-[11.5px]" data-testid="sh-lock-input" />
          <Button size="sm" onClick={lock} className="h-8 text-[11px]" data-testid="sh-lock-btn">Save</Button>
        </div>
        <div className="text-[10.5px] text-slate-500 mt-1">Leave blank + Save to clear the lock.</div>
      </div>
      {d.stage !== "approved" && d.stage !== "rejected" && (
        <div className="border border-rose-200 rounded-md p-3">
          <Label className="text-[10.5px] uppercase tracking-wider font-semibold text-rose-700 flex items-center gap-1"><XCircle size={11} /> Reject request</Label>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (shown in log)" className="h-8 text-[11.5px] mt-1.5" data-testid="sh-reject-reason" />
          <Button size="sm" variant="outline" onClick={reject} className="h-8 text-[11px] mt-1.5 text-rose-700 border-rose-300 hover:bg-rose-50" data-testid="sh-reject-btn">Reject</Button>
        </div>
      )}
    </div>
  );
}

// === Small field helpers ===
function FldText({ label, value, onChange, tid }) {
  return (
    <div>
      <Label className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-600">{label} *</Label>
      <Textarea value={value} onChange={(e) => onChange(e.target.value)} rows={2} className="mt-1 text-[12px]" data-testid={tid} />
    </div>
  );
}
function FldInput({ label, value, onChange, type = "text", tid }) {
  return (
    <div>
      <Label className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-600">{label} *</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 h-9 text-[12px]" data-testid={tid} />
    </div>
  );
}
function FldSelect({ label, value, onChange, opts, tid }) {
  return (
    <div>
      <Label className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-600">{label} *</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-1 h-9 text-[12px]" data-testid={tid}><SelectValue placeholder="Select…" /></SelectTrigger>
        <SelectContent>{opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

// === Employee-side dialog mounted on Dashboard ===
export function SalaryHikeSubmitDialog({ open, onClose, onSubmitted }) {
  const [stepWarning, setStepWarning] = useState(true);
  const [form, setForm] = useState({
    reason: "", achievements: "", additional_responsibilities: "",
    measurable_contributions: [], certifications: "", challenges_overcome: "",
    next_goals: "", requested_percentage_or_amount: "", justification: "",
  });
  const set = (k, v) => setForm({ ...form, [k]: v });
  const toggleContribution = (c) => {
    const list = form.measurable_contributions || [];
    set("measurable_contributions", list.includes(c) ? list.filter((x) => x !== c) : [...list, c]);
  };
  const submit = async () => {
    for (const k of ["reason", "achievements", "additional_responsibilities", "certifications",
      "challenges_overcome", "next_goals", "requested_percentage_or_amount", "justification"]) {
      if (!(form[k] || "").trim()) { toast.error(`${k.replace(/_/g, " ")} is required`); return; }
    }
    if (!form.measurable_contributions?.length) { toast.error("Pick at least one measurable contribution"); return; }
    try {
      await api.post("/salary-hike/requests", { form });
      toast.success("Salary Hike Request submitted");
      onSubmitted();
    } catch (e) { toast.error(formatError(e)); }
  };

  if (stepWarning) {
    return (
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-md" data-testid="sh-warning-dialog">
          <DialogHeader>
            <DialogTitle className="heading">Before you submit</DialogTitle>
          </DialogHeader>
          <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-[13px] text-amber-900 leading-relaxed">
            <b>Please note:</b> Do not submit multiple requests. You need to submit your hike
            request in the month of <b>August to September</b>. If the button is locked, it
            will only open after 3 months from today&rsquo;s date.<br /><br />
            <b>Do you still want to submit?</b>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onClose} data-testid="sh-warning-no">No, take me back</Button>
            <Button onClick={() => setStepWarning(false)} className="bg-violet-600 hover:bg-violet-700" data-testid="sh-warning-yes">Yes, continue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="sh-form-dialog">
        <DialogHeader><DialogTitle className="heading">Salary Increment Request</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <FldSelect label="What is the reason for requesting a salary increase?" value={form.reason} onChange={(v) => set("reason", v)}
            opts={["Outstanding Performance", "Increased Responsibilities", "Promotion", "Market Salary Adjustment", "Cost of Living Adjustment", "Other"]}
            tid="sh-reason" />
          <FldText label="What achievements have you accomplished in the last review period?" value={form.achievements} onChange={(v) => set("achievements", v)} tid="sh-achievements" />
          <FldText label="Have you taken on any additional responsibilities beyond your current role? Please explain." value={form.additional_responsibilities} onChange={(v) => set("additional_responsibilities", v)} tid="sh-resp" />
          <div>
            <Label className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-600">Measurable contributions * (pick all that apply)</Label>
            <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="sh-contribs">
              {["Revenue Generated", "Cost Savings", "Process Improvements", "Team Leadership", "Client Satisfaction"].map((c) => (
                <button key={c} type="button" onClick={() => toggleContribution(c)}
                  className={`px-2.5 py-1 rounded-full text-[10.5px] font-semibold border ${form.measurable_contributions?.includes(c) ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-700 border-slate-200"}`}
                  data-testid={`sh-contrib-${c.replace(/\s+/g, "-")}`}>{c}</button>
              ))}
            </div>
          </div>
          <FldText label="Have you completed any certifications, training programs, or skill development courses recently?" value={form.certifications} onChange={(v) => set("certifications", v)} tid="sh-certs" />
          <FldText label="What challenges have you successfully overcome in your role?" value={form.challenges_overcome} onChange={(v) => set("challenges_overcome", v)} tid="sh-chal" />
          <FldText label="What are your key goals for the next 6-12 months?" value={form.next_goals} onChange={(v) => set("next_goals", v)} tid="sh-goals" />
          <FldInput label="What percentage or amount of salary increase are you requesting?" value={form.requested_percentage_or_amount} onChange={(v) => set("requested_percentage_or_amount", v)} tid="sh-req" />
          <FldText label="Why do you believe this salary increase is justified?" value={form.justification} onChange={(v) => set("justification", v)} tid="sh-just" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} className="bg-violet-600 hover:bg-violet-700" data-testid="sh-submit">Submit Request</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
